import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, PageHeader, Badge, Button } from "@/components/ui-kit";
import { Check, X, UserPlus, RefreshCw, Building2, Phone, MapPin, FileText } from "lucide-react";
import { formatDateTime } from "@/lib/format";

export default function PendingSignups() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/pending-signups");
      setRows(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat pendaftaran");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    if (!window.confirm("Setujui pendaftaran ini? Akun akan langsung aktif dan klien akan menerima email konfirmasi.")) return;
    setBusy(id);
    try { await api.post(`/admin/pending-signups/${id}/approve`); toast.success("Disetujui & email aktivasi terkirim"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyetujui"); }
    finally { setBusy(""); }
  };
  const reject = async (id) => {
    if (!window.confirm("Tolak pendaftaran ini? Tindakan tidak bisa di-undo dari panel ini.")) return;
    setBusy(id);
    try { await api.delete(`/admin/pending-signups/${id}`); toast.success("Pendaftaran ditolak"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menolak"); }
    finally { setBusy(""); }
  };

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader
        overline="Admin"
        title="Pendaftaran Klien Menunggu Persetujuan"
        description="Tinjau pendaftaran klien yang sudah memverifikasi email. Setujui untuk mengaktifkan akun."
        action={<Button onClick={load} className="!text-xs"><RefreshCw className="h-3 w-3" />Refresh</Button>}
      />
      {loading ? (
        <Card className="p-12 text-center text-stone-400">Memuat…</Card>
      ) : rows.length === 0 ? (
        <Card className="p-16 text-center text-stone-400" data-testid="pending-empty">
          <UserPlus className="h-8 w-8 mx-auto mb-3 text-stone-300" />
          Tidak ada pendaftaran yang menunggu persetujuan.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.map((p) => (
            <Card key={p.id} className="p-5 hover:border-terra-300 transition-colors" data-testid={`pending-${p.id}`}>
              <div className="flex items-start gap-4">
                {p.photo_b64 ? (
                  <img src={`data:image/png;base64,${p.photo_b64}`} alt={p.name} className="h-14 w-14 object-cover border border-stone-200" />
                ) : (
                  <div className="h-14 w-14 bg-gradient-to-br from-terra-100 to-stone-100 flex items-center justify-center font-display text-2xl font-bold text-stone-400 border border-stone-200">
                    {p.name?.slice(0, 1)?.toUpperCase() || "?"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg font-semibold text-stone-900 truncate">{p.name}</h3>
                    {p.existing_client_id ? (
                      <Badge tone="success">Klien existing</Badge>
                    ) : (
                      <Badge tone="terra">Klien baru</Badge>
                    )}
                  </div>
                  <p className="text-xs font-mono text-stone-600 truncate">{p.email}</p>
                  <p className="text-[10px] text-stone-400 mt-1">Daftar: {formatDateTime(p.created_at)}</p>
                </div>
              </div>

              <div className="mt-4 space-y-1.5 text-xs">
                <DetailLine icon={Building2} label="Perusahaan" value={p.client_name} />
                {p.npwp && <DetailLine icon={FileText} label="NPWP" value={p.npwp} mono />}
                {p.address && <DetailLine icon={MapPin} label="Alamat" value={p.address} />}
                {p.phone && <DetailLine icon={Phone} label="Telepon" value={p.phone} mono />}
              </div>

              <div className="flex items-center gap-2 mt-5 pt-3 border-t border-stone-100">
                <button
                  onClick={() => approve(p.id)}
                  disabled={busy === p.id}
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider font-semibold bg-emerald-600 hover:bg-emerald-700 text-white py-2 disabled:opacity-50"
                  data-testid={`pending-approve-${p.id}`}
                >
                  <Check className="h-3 w-3" /> Setujui
                </button>
                <button
                  onClick={() => reject(p.id)}
                  disabled={busy === p.id}
                  className="flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider text-red-700 hover:bg-red-50 border border-red-200 px-3 py-2 disabled:opacity-50"
                  data-testid={`pending-reject-${p.id}`}
                >
                  <X className="h-3 w-3" /> Tolak
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function DetailLine({ icon: Icon, label, value, mono = false }) {
  return (
    <div className="flex items-start gap-1.5 text-stone-700">
      <Icon className="h-3 w-3 text-stone-400 mt-0.5 shrink-0" />
      <span className="text-stone-400 uppercase tracking-wider text-[10px] mr-1 shrink-0">{label}:</span>
      <span className={`flex-1 ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
